import { useState, useEffect, useRef } from "react"
import { useSearch } from "wouter"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { useInfiniteQuery, useMutation } from "@tanstack/react-query"
import { queryClient, apiRequest } from "@/lib/queryClient"
import type { PillarPost } from "@shared/schema"
import { isSignalRoomSourced } from "@shared/signalRoom"
import { useToast } from "@/hooks/use-toast"
import { statusConfig } from "@/lib/status-config"
import { formatLabel } from "@shared/pillarFormats"
import {
  Columns3,
  Filter,
  Eye,
  Clock,
  CalendarClock,
  Send,
  Hash,
  Sparkles,
  Trash2
} from "lucide-react"
import { FaLinkedin, FaInstagram, FaFacebook } from "react-icons/fa"
import { HotelPostDialog } from "@/components/instagram/HotelPostDialog"
import { EDIT_HINT, type HotelPost } from "@/components/instagram/hotelPosts"
import { Skeleton } from "@/components/ui/skeleton"
import { FloatingCard } from "@/components/FloatingCard"
import { StatCard } from "@/components/StatCard"
import { ScheduledPlanLink } from "@/components/ScheduledPlanLink"
import { AnimatedSection } from "@/components/AnimatedSection"
import { NewsPostsModal } from "@/components/NewsPostsModal"
import { isValidTitle, extractTitleFromContent } from "@/lib/postTitle"
import { format } from "date-fns"
import { de } from "date-fns/locale"

interface PillarPostPage {
  items: PillarPost[]
  total: number
  stats: { total: number; pending: number; scheduled: number; posted: number; rejected: number }
}

const pillarTypeConfig = {
  context_setting: { label: "Context Setting", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" },
  capability_building: { label: "Capability Building", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  solution_supporting: { label: "Solution Supporting", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" }
} as const

// Hotel-Edition B1 (PLAN.md §7.3): the same page as "LinkedIn-Beiträge" for hotels — server filter
// platform=linkedin, platform icon per post, no B2B type badges/filter and no "Signal Room" badge, German
// texts, edit/delete via the DFY routes. Without a variant (agencies, /pillar-posts) nothing changes.
type PillarPostsVariant = "default" | "hotel-linkedin"

const PLATFORM_ICON = { linkedin: FaLinkedin, instagram: FaInstagram, facebook: FaFacebook } as const

export default function PillarPostsPage({ variant = "default" }: { variant?: PillarPostsVariant } = {}) {
  const hotel = variant === "hotel-linkedin"
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [selectedPillarType, setSelectedPillarType] = useState<string>("all")
  const [selectedPost, setSelectedPost] = useState<PillarPost | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deletePost, setDeletePost] = useState<PillarPost | null>(null)
  const { toast } = useToast()

  const handleViewPost = (post: PillarPost) => {
    setSelectedPost(post)
    setIsModalOpen(true)
  }

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open)
    if (!open) {
      setSelectedPost(null)
    }
  }

  const deleteMutation = useMutation({
    mutationFn: async (postId: string) => {
      return apiRequest("DELETE", `/api/pillar-posts/${postId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pillar-posts'] })
      toast({
        title: "Post gelöscht",
        description: hotel ? "Der Beitrag wurde gelöscht." : "Der Pillar Post wurde erfolgreich gelöscht."
      })
      setDeletePost(null)
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error?.data?.error || "Der Post konnte nicht gelöscht werden.",
        variant: "destructive"
      })
    }
  })

  // Server-paged {items,total,stats} (slice-4 hardening) — filters, sort and stats live in SQL;
  // the page only appends. refetchOnWindowFocus:'always' because the global staleTime is Infinity.
  const PAGE_SIZE = 20
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<PillarPostPage>({
    queryKey: ['/api/pillar-posts', { status: selectedStatus, pillarType: selectedPillarType, ...(hotel ? { module: "linkedin" } : {}) }],
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(pageParam ?? 0) })
      if (selectedStatus !== "all") params.set("status", selectedStatus)
      if (selectedPillarType !== "all" && !hotel) params.set("pillarType", selectedPillarType)
      if (hotel) params.set("platform", "linkedin")
      const res = await fetch(`/api/pillar-posts?${params.toString()}`, { credentials: "include" })
      if (!res.ok) throw new Error("Failed to fetch pillar posts")
      return res.json()
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return loaded < lastPage.total ? loaded : undefined
    },
    refetchOnWindowFocus: 'always',
  })
  const filteredPosts = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0
  const stats = data?.pages[0]?.stats ?? { total: 0, pending: 0, scheduled: 0, posted: 0, rejected: 0 }

  // Deep-link from the Posting-Plan: ?post=<id> auto-opens that post's modal. Driven by the
  // router's search string (a second deep link while mounted re-triggers), keyed on the handled
  // id (closing the modal or a refetch doesn't re-open it), stale fetches are ignored.
  const search = useSearch()
  const handledDeepLinkRef = useRef<string | null>(null)
  useEffect(() => {
    if (isLoading) return
    const id = new URLSearchParams(search).get("post")
    if (!id || handledDeepLinkRef.current === id) return
    handledDeepLinkRef.current = id
    const loaded = filteredPosts.find((p) => p.id === id)
    if (loaded) {
      setSelectedPost(loaded)
      setIsModalOpen(true)
      return
    }
    let cancelled = false
    fetch(`/api/pillar-posts/${id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((post: PillarPost | null) => {
        if (cancelled) return
        if (post) {
          setSelectedPost(post)
          setIsModalOpen(true)
        } else {
          toast({ title: "Beitrag nicht gefunden", description: hotel ? "Den verlinkten Beitrag gibt es nicht mehr." : "Der verlinkte Pillar Post existiert nicht mehr.", variant: "destructive" })
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [search, isLoading, filteredPosts, toast])

  const formatDate = (date: Date | string | null) => {
    if (!date) return ""
    return format(new Date(date), "dd. MMM yyyy", { locale: de })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{hotel ? "Die Beiträge konnten nicht geladen werden." : "Fehler beim Laden der Pillar Posts."}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <AnimatedSection>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="heading-pillar-posts">
              {hotel ? <FaLinkedin className="h-8 w-8 text-blue-600" /> : <Columns3 className="h-8 w-8 text-blue-600" />}
              {hotel ? "LinkedIn-Beiträge" : "Beiträge nach Themen"}
            </h1>
            <p className="text-muted-foreground mt-2">
              {hotel ? `Ihre Beiträge für LinkedIn. ${EDIT_HINT}` : "KI-Beiträge auf Grundlage Ihrer Content-Säulen"}
            </p>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title={hotel ? "Alle Beiträge" : "Gesamt Posts"}
            value={stats.total}
            hint={hotel ? "LinkedIn-Beiträge" : "Pillar-basierte Posts"}
            icon={Columns3}
            testId="stat-total-posts"
            active={selectedStatus === "all"}
            onClick={() => setSelectedStatus("all")}
          />
          <StatCard
            title="Ausstehend"
            value={stats.pending}
            hint="Zur Überprüfung"
            icon={Clock}
            testId="stat-pending"
            active={selectedStatus === "pending"}
            onClick={() => setSelectedStatus("pending")}
          />
          <StatCard
            title="Geplant"
            value={stats.scheduled}
            hint="Für später eingeplant"
            icon={CalendarClock}
            testId="stat-scheduled"
            active={selectedStatus === "scheduled"}
            onClick={() => setSelectedStatus("scheduled")}
          />
          <StatCard
            title="Gepostet"
            value={stats.posted}
            hint="Veröffentlicht"
            icon={Send}
            testId="stat-posted"
            active={selectedStatus === "posted"}
            onClick={() => setSelectedStatus("posted")}
          />
        </div>
      </AnimatedSection>

      <AnimatedSection delay={200}>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-56" data-testid="select-status-filter">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="scheduled">Geplant</SelectItem>
                <SelectItem value="posted">Gepostet</SelectItem>
                <SelectItem value="rejected">Abgelehnt</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {!hotel && <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedPillarType} onValueChange={setSelectedPillarType}>
              <SelectTrigger className="w-48" data-testid="select-pillar-filter">
                <SelectValue placeholder="Säule filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Säulen</SelectItem>
                <SelectItem value="context_setting">Context Setting</SelectItem>
                <SelectItem value="capability_building">Capability Building</SelectItem>
                <SelectItem value="solution_supporting">Solution Supporting</SelectItem>
              </SelectContent>
            </Select>
          </div>}

          <span className="text-sm text-muted-foreground ml-auto" data-testid="text-result-count">
            {filteredPosts.length} von {total} angezeigt
          </span>
        </div>
      </AnimatedSection>

      <AnimatedSection delay={300}>
        {filteredPosts.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Columns3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">{hotel ? "Keine Beiträge gefunden" : "Keine Pillar Posts gefunden"}</h3>
              <p className="text-muted-foreground">
                {stats.total === 0
                  ? (hotel ? "Sobald Ihr Team LinkedIn-Beiträge vorbereitet hat, erscheinen sie hier." : "Es wurden noch keine Pillar Posts generiert.")
                  : "Keine Beiträge entsprechen Ihren Filterkriterien."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPosts.map((post) => {
              const status = statusConfig[post.status as keyof typeof statusConfig] || statusConfig.pending
              const pillarType = pillarTypeConfig[post.pillarType as keyof typeof pillarTypeConfig] || pillarTypeConfig.context_setting
              const StatusIcon = status.icon
              // DFY (Wochenplan) rows are locked to the email-approval flow — read-only here.
              const isDfy = post.cycleId != null
              const cardTitle = isValidTitle(post.title) ? post.title! : extractTitleFromContent(post.postContent)
              const PlatformIcon = hotel ? (PLATFORM_ICON[post.platform as keyof typeof PLATFORM_ICON] ?? FaLinkedin) : FaLinkedin

              return (
                <div key={post.id} className="h-full" onClick={() => handleViewPost(post)}>
                  <FloatingCard clickable className="h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <PlatformIcon className="h-4 w-4 text-blue-600" />
                        {!hotel && (
                          <Badge className={pillarType.color} variant="outline">
                            {pillarType.label}
                          </Badge>
                        )}
                      </div>
                      <Badge className={status.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                    {isDfy && (
                      <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 w-fit mt-2" data-testid={`badge-dfy-${post.id}`}>
                        <Sparkles className="h-3 w-3 mr-1" />
                        Von Lina erstellt
                      </Badge>
                    )}
                    {!hotel && isSignalRoomSourced(post.sourceRef) && (
                      <Badge className="bg-pink-100 text-pink-800 border-pink-200 w-fit mt-2" data-testid={`badge-signal-room-${post.id}`}>
                        Signal Room
                      </Badge>
                    )}
                    <CardTitle className="text-base line-clamp-2 mt-2" data-testid={`post-title-${post.id}`}>
                      {cardTitle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {post.imageUrl && (
                      <div className="relative aspect-video bg-muted rounded-lg overflow-hidden">
                        <img
                          src={post.imageUrl}
                          alt={cardTitle}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {post.postContent}
                    </p>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="secondary" className="text-xs max-w-[220px] truncate">
                        {post.pillarName}
                      </Badge>
                      {post.format && (
                        <Badge variant="outline" className="text-xs" data-testid={`badge-format-${post.id}`}>
                          {formatLabel(post.format)}
                        </Badge>
                      )}
                    </div>

                    {Array.isArray(post.hashtags) && (post.hashtags as string[]).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(post.hashtags as string[]).slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            <Hash className="h-2 w-2 mr-1" />
                            {String(tag).replace('#', '')}
                          </Badge>
                        ))}
                        {(post.hashtags as string[]).length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{(post.hashtags as string[]).length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    {post.scheduledFor && (post.status === 'scheduled' || post.status === 'posted') && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <ScheduledPlanLink
                          postId={post.id}
                          scheduledFor={post.scheduledFor}
                          label={post.status === 'posted' ? 'Veröffentlicht am' : 'Geplant für'}
                          testId={`badge-scheduled-${post.id}`}
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(post.createdAt)}
                      </span>
                      <div className="flex items-center gap-1">
                        {!isDfy && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            data-testid={`button-delete-post-${post.id}`}
                            title="Post löschen"
                            aria-label="Post löschen"
                            onClick={(e) => {
                              e.stopPropagation()
                              setDeletePost(post)
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`button-view-post-${post.id}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleViewPost(post)
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ansehen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                  </FloatingCard>
                </div>
              )
            })}
          </div>
        )}
        {hasNextPage && (
          <div className="flex justify-center pt-4">
            <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} data-testid="button-load-more">
              {isFetchingNextPage ? "Lädt..." : "Mehr laden"}
            </Button>
          </div>
        )}
      </AnimatedSection>

      {hotel && (
        <HotelPostDialog
          // Freshest row after a background refetch (version/editability), review he-b1 F4.
          post={(selectedPost ? filteredPosts.find((p) => p.id === selectedPost.id) ?? selectedPost : null) as HotelPost | null}
          open={isModalOpen && !!selectedPost}
          onOpenChange={handleModalClose}
        />
      )}

      {!hotel && selectedPost && (
        <NewsPostsModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          newsItem={selectedPost}
          sourceType="pillar_post"
          readOnly={selectedPost.cycleId != null}
        />
      )}

      <AlertDialog open={!!deletePost} onOpenChange={(open) => !open && setDeletePost(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Post löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {hotel ? "Möchten Sie diesen Beitrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden." : "Möchten Sie diesen Pillar Post wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePost && deleteMutation.mutate(deletePost.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
